import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

// Tables aren't in generated types yet — use an untyped client.
const sb = () => supabaseAdmin as unknown as {
  from: (t: string) => any;
};

export type HelpCategory = {
  id: string;
  slug: string;
  parent_id: string | null;
  icon: string | null;
  sort_order: number;
  name: string;
  description: string | null;
  article_count?: number;
};

export type HelpArticle = {
  id: string;
  slug: string;
  category_id: string;
  updated_at: string;
  title: string;
  excerpt: string | null;
  body_md: string;
  meta_title: string | null;
  meta_description: string | null;
};

export const DEFAULT_LOCALE = "en";

/** Pick translation row for the requested locale, with English fallback. */
function pickTr(rows: any[] | undefined, locale: string): any {
  if (!rows || rows.length === 0) return undefined;
  return rows.find((t: any) => t.locale === locale)
    ?? rows.find((t: any) => t.locale === DEFAULT_LOCALE)
    ?? rows[0];
}

/** Resolve the slug used in URLs for a given record + locale (translation slug → base slug). */
function localizedSlug(baseSlug: string, translations: any[] | undefined, locale: string): string {
  const tr = (translations ?? []).find((t: any) => t.locale === locale);
  return (tr?.slug && String(tr.slug).length > 0) ? tr.slug : baseSlug;
}

/** Build the URL prefix for a locale: /help/{locale} for non-en, /help for en. */
export function helpHrefBase(locale: string): string {
  return locale === DEFAULT_LOCALE ? "/help" : `/help/${locale}`;
}

/** Build full help URL for a list of path segments + locale. */
export function buildHelpHref(locale: string, segments: string[]): string {
  const base = helpHrefBase(locale);
  if (segments.length === 0) return base;
  return `${base}/${segments.join("/")}`;
}

/** Detect a locale prefix at the start of a /help/* splat. Returns { locale, rest }. */
export function extractLocaleFromSplat(splat: string, knownLocales: string[]): { locale: string; rest: string } {
  const parts = splat.split("/").filter(Boolean);
  if (parts.length === 0) return { locale: DEFAULT_LOCALE, rest: "" };
  const first = parts[0];
  if (first !== DEFAULT_LOCALE && knownLocales.includes(first)) {
    return { locale: first, rest: parts.slice(1).join("/") };
  }
  return { locale: DEFAULT_LOCALE, rest: parts.join("/") };
}

/** Fetch the full help-center tree (categories + counts) for the landing page. */
export const getHelpTree = createServerFn({ method: "GET" })
  .inputValidator((d: { locale?: string } | undefined) =>
    z.object({ locale: z.string().min(2).max(8).optional() }).parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    const locale = data.locale ?? DEFAULT_LOCALE;
    try {
      const [cats, arts] = await Promise.all([
        sb()
          .from("help_categories")
          .select("id, slug, parent_id, icon, sort_order, help_category_translations(name, description, slug, locale)")
          .order("sort_order"),
        sb()
          .from("help_articles")
          .select("id, slug, category_id, updated_at, published, help_article_translations(title, excerpt, slug, locale)")
          .eq("published", true),
      ]);

      const categories: HelpCategory[] = (cats.data ?? []).map((c: any) => {
        const tr = pickTr(c.help_category_translations, locale);
        const slug = localizedSlug(c.slug, c.help_category_translations, locale);
        return {
          id: c.id, slug, parent_id: c.parent_id, icon: c.icon, sort_order: c.sort_order,
          name: tr?.name ?? c.slug, description: tr?.description ?? null,
          article_count: 0,
        };
      });
      const articles = (arts.data ?? []).map((a: any) => {
        const tr = pickTr(a.help_article_translations, locale);
        const slug = localizedSlug(a.slug, a.help_article_translations, locale);
        return {
          id: a.id, slug, category_id: a.category_id, updated_at: a.updated_at,
          title: tr?.title ?? a.slug, excerpt: tr?.excerpt ?? null,
        };
      });
      const counts = new Map<string, number>();
      for (const a of articles) counts.set(a.category_id, (counts.get(a.category_id) ?? 0) + 1);
      const byId = new Map(categories.map((c) => [c.id, c]));
      for (const c of categories) {
        let cur: HelpCategory | undefined = c;
        const own = counts.get(c.id) ?? 0;
        while (cur) {
          cur.article_count = (cur.article_count ?? 0) + own;
          cur = cur.parent_id ? byId.get(cur.parent_id) : undefined;
        }
      }
      return { categories, articles };
    } catch (e) {
      console.error("[help] getHelpTree failed", e);
      return { categories: [] as HelpCategory[], articles: [] as Array<Pick<HelpArticle, "id" | "slug" | "category_id" | "updated_at" | "title" | "excerpt">> };
    }
  });

/** Resolve a /help/* path to either a category page, an article page, or a 301 redirect. */
export const getHelpByPath = createServerFn({ method: "GET" })
  .inputValidator((d: { path: string; locale?: string }) =>
    z.object({ path: z.string().min(1).max(500), locale: z.string().min(2).max(8).optional() }).parse(d),
  )
  .handler(async ({ data }) => {
    const locale = data.locale ?? DEFAULT_LOCALE;
    const segments = data.path.split("/").filter(Boolean);
    if (segments.length === 0) return { type: "none" as const };

    try {
      const { data: allCats } = await sb()
        .from("help_categories")
        .select("id, slug, parent_id, icon, sort_order, help_category_translations(name, description, meta_title, meta_description, slug, locale)");
      const cats: any[] = allCats ?? [];

      const matchCat = (seg: string, parentId: string | null) =>
        cats.find((c: any) => {
          if (c.parent_id !== parentId) return false;
          const tr = (c.help_category_translations ?? []).find((t: any) => t.locale === locale);
          if (tr?.slug) return tr.slug === seg;
          return c.slug === seg;
        });

      const matchCatAnySlug = (seg: string, parentId: string | null) =>
        cats.find((c: any) => {
          if (c.parent_id !== parentId) return false;
          if (c.slug === seg) return true;
          return (c.help_category_translations ?? []).some((t: any) => t.slug === seg);
        });

      const path: any[] = [];
      let parentId: string | null = null;
      let consumed = 0;
      let usedFallback = false;
      for (const seg of segments) {
        let match = matchCat(seg, parentId);
        if (!match) {
          match = matchCatAnySlug(seg, parentId);
          if (match) usedFallback = true;
          else break;
        }
        path.push(match);
        parentId = match.id;
        consumed++;
      }

      const catSegmentsForLocale = (chain: any[], loc: string): string[] =>
        chain.map((c) => localizedSlug(c.slug, c.help_category_translations, loc));

      const canonicalPathByLocale = (chain: any[], articleRow?: any): Record<string, string> => {
        const result: Record<string, string> = {};
        const articleLocales = articleRow
          ? new Set<string>(((articleRow.help_article_translations ?? []) as any[]).map((t) => t.locale).concat([DEFAULT_LOCALE]))
          : null;
        const leafCatLocales = !articleRow && chain.length > 0
          ? new Set<string>(((chain[chain.length - 1].help_category_translations ?? []) as any[]).map((t) => t.locale).concat([DEFAULT_LOCALE]))
          : null;
        const candidates = articleLocales ?? leafCatLocales ?? new Set<string>([DEFAULT_LOCALE]);
        for (const loc of candidates) {
          const segs = catSegmentsForLocale(chain, loc);
          if (articleRow) segs.push(localizedSlug(articleRow.slug, articleRow.help_article_translations, loc));
          result[loc] = buildHelpHref(loc, segs);
        }
        return result;
      };

      // Fully matched → category page
      if (consumed === segments.length && path.length > 0) {
        const leaf = path[path.length - 1];
        const tr = pickTr(leaf.help_category_translations, locale);
        const canonicalSegs = catSegmentsForLocale(path, locale);
        const canonicalUrl = buildHelpHref(locale, canonicalSegs);
        if (usedFallback && canonicalUrl !== buildHelpHref(locale, segments)) {
          return { type: "redirect" as const, to: canonicalUrl };
        }
        const { data: arts } = await sb()
          .from("help_articles")
          .select("id, slug, category_id, updated_at, help_article_translations(title, excerpt, slug, locale)")
          .eq("published", true)
          .eq("category_id", leaf.id);
        const subCats = cats
          .filter((c: any) => c.parent_id === leaf.id)
          .map((c: any) => {
            const ctr = pickTr(c.help_category_translations, locale);
            const slug = localizedSlug(c.slug, c.help_category_translations, locale);
            return { id: c.id, slug, name: ctr?.name ?? c.slug, description: ctr?.description ?? null, icon: c.icon, articles: [] as Array<{ id: string; slug: string; title: string }> };
          });
        if (subCats.length > 0) {
          const subIds = subCats.map((s: any) => s.id);
          const { data: subArts } = await sb()
            .from("help_articles")
            .select("id, slug, category_id, help_article_translations(title, slug, locale)")
            .eq("published", true)
            .in("category_id", subIds);
          const bySub = new Map<string, any[]>();
          for (const a of subArts ?? []) {
            const atr = pickTr((a as any).help_article_translations, locale);
            const slug = localizedSlug((a as any).slug, (a as any).help_article_translations, locale);
            const list = bySub.get((a as any).category_id) ?? [];
            list.push({ id: (a as any).id, slug, title: atr?.title ?? (a as any).slug });
            bySub.set((a as any).category_id, list);
          }
          for (const s of subCats) s.articles = (bySub.get(s.id) ?? []).slice(0, 4);
        }
        const articles = (arts ?? []).map((a: any) => {
          const atr = pickTr(a.help_article_translations, locale);
          const slug = localizedSlug(a.slug, a.help_article_translations, locale);
          return { id: a.id, slug, title: atr?.title ?? a.slug, excerpt: atr?.excerpt ?? null, updated_at: a.updated_at };
        });
        return {
          type: "category" as const,
          locale,
          breadcrumbs: path.map((c: any) => {
            const t = pickTr(c.help_category_translations, locale);
            const slug = localizedSlug(c.slug, c.help_category_translations, locale);
            return { slug, name: t?.name ?? c.slug };
          }),
          category: {
            id: leaf.id, slug: leaf.slug, name: tr?.name ?? leaf.slug,
            description: tr?.description ?? null,
            meta_title: tr?.meta_title ?? null, meta_description: tr?.meta_description ?? null,
          },
          subcategories: subCats,
          articles,
          canonicalPathByLocale: canonicalPathByLocale(path),
        };
      }

      if (path.length === 0) return { type: "none" as const };
      const articleSlug = segments[consumed];
      if (!articleSlug || consumed !== segments.length - 1) return { type: "none" as const };
      const leaf = path[path.length - 1];

      const { data: artsForCat } = await sb()
        .from("help_articles")
        .select("id, slug, category_id, updated_at, help_article_translations(title, excerpt, body_md, meta_title, meta_description, slug, locale)")
        .eq("category_id", leaf.id)
        .eq("published", true);
      const articleRows: any[] = artsForCat ?? [];
      let art: any = articleRows.find((a) => {
        const tr = (a.help_article_translations ?? []).find((t: any) => t.locale === locale);
        return tr?.slug ? tr.slug === articleSlug : a.slug === articleSlug;
      });
      let articleUsedFallback = false;
      if (!art) {
        art = articleRows.find((a) =>
          a.slug === articleSlug
          || (a.help_article_translations ?? []).some((t: any) => t.slug === articleSlug)
        );
        if (art) articleUsedFallback = true;
      }
      if (!art) return { type: "none" as const };

      if (usedFallback || articleUsedFallback) {
        const segs = catSegmentsForLocale(path, locale);
        segs.push(localizedSlug(art.slug, art.help_article_translations, locale));
        const to = buildHelpHref(locale, segs);
        if (to !== buildHelpHref(locale, segments)) {
          return { type: "redirect" as const, to };
        }
      }

      const atr = pickTr(art.help_article_translations, locale);
      return {
        type: "article" as const,
        locale,
        breadcrumbs: path.map((c: any) => {
          const t = pickTr(c.help_category_translations, locale);
          const slug = localizedSlug(c.slug, c.help_category_translations, locale);
          return { slug, name: t?.name ?? c.slug };
        }),
        article: {
          id: art.id,
          slug: localizedSlug(art.slug, art.help_article_translations, locale),
          updated_at: art.updated_at,
          title: atr?.title ?? articleSlug,
          excerpt: atr?.excerpt ?? null,
          body_md: atr?.body_md ?? "",
          meta_title: atr?.meta_title ?? null,
          meta_description: atr?.meta_description ?? null,
        },
        canonicalPathByLocale: canonicalPathByLocale(path, art),
      };
    } catch (e) {
      console.error("[help] getHelpByPath failed", e);
      return { type: "none" as const };
    }
  });

/** Distinct locales present anywhere in help_*_translations. Always includes "en". */
export const getAvailableHelpLocales = createServerFn({ method: "GET" })
  .handler(async () => {
    try {
      const [a, c] = await Promise.all([
        sb().from("help_article_translations").select("locale"),
        sb().from("help_category_translations").select("locale"),
      ]);
      const set = new Set<string>([DEFAULT_LOCALE]);
      for (const r of a.data ?? []) if (r?.locale) set.add(r.locale);
      for (const r of c.data ?? []) if (r?.locale) set.add(r.locale);
      return { locales: Array.from(set).sort() };
    } catch (e) {
      console.error("[help] getAvailableHelpLocales failed", e);
      return { locales: [DEFAULT_LOCALE] };
    }
  });

/**
 * Every help URL × locale combination, for the sitemap.
 * Returns one record per category and per article, with the URL in each locale
 * that has a translation row for that record (English is always included).
 * Empty categories (no published articles directly or in any descendant) are
 * omitted so the sitemap stays in sync with routes that actually serve content.
 */
export const getAllHelpUrls = createServerFn({ method: "GET" })
  .handler(async () => {
    try {
      const [{ data: cats }, { data: arts }] = await Promise.all([
        sb().from("help_categories")
          .select("id, slug, parent_id, help_category_translations(slug, locale)"),
        sb().from("help_articles")
          .select("id, slug, category_id, updated_at, published, help_article_translations(slug, locale)")
          .eq("published", true),
      ]);

      const catList: any[] = cats ?? [];
      const catById = new Map<string, any>(catList.map((c) => [c.id, c]));
      const childrenOf = (parentId: string | null) => catList.filter((c) => c.parent_id === parentId);

      // Count published articles per category, including all descendants.
      const articleCountByCat = new Map<string, number>();
      for (const a of (arts ?? [])) {
        articleCountByCat.set(a.category_id, (articleCountByCat.get(a.category_id) ?? 0) + 1);
      }
      const countWithDescendants = (catId: string): number => {
        let count = articleCountByCat.get(catId) ?? 0;
        for (const child of childrenOf(catId)) {
          count += countWithDescendants(child.id);
        }
        return count;
      };
      const visibleCatIds = new Set<string>(
        catList.filter((c) => countWithDescendants(c.id) > 0).map((c) => c.id),
      );

      const chainOf = (catId: string): any[] => {
        const chain: any[] = [];
        let cur = catById.get(catId);
        while (cur) {
          chain.unshift(cur);
          cur = cur.parent_id ? catById.get(cur.parent_id) : undefined;
        }
        return chain;
      };

      type Entry = {
        kind: "category" | "article";
        updated_at?: string;
        pathByLocale: Record<string, string>;
      };
      const out: Entry[] = [];

      for (const c of catList) {
        if (!visibleCatIds.has(c.id)) continue;
        const chain = chainOf(c.id);
        const leafLocales = new Set<string>([DEFAULT_LOCALE]);
        for (const t of (c.help_category_translations ?? [])) if (t?.locale) leafLocales.add(t.locale);
        const pathByLocale: Record<string, string> = {};
        for (const loc of leafLocales) {
          const segs = chain.map((cc) => localizedSlug(cc.slug, cc.help_category_translations, loc));
          pathByLocale[loc] = buildHelpHref(loc, segs);
        }
        out.push({ kind: "category", pathByLocale });
      }

      for (const a of (arts ?? [])) {
        const chain = chainOf(a.category_id);
        if (chain.length === 0 || !visibleCatIds.has(a.category_id)) continue;
        const articleLocales = new Set<string>([DEFAULT_LOCALE]);
        for (const t of (a.help_article_translations ?? [])) if (t?.locale) articleLocales.add(t.locale);
        const pathByLocale: Record<string, string> = {};
        for (const loc of articleLocales) {
          const segs = chain.map((cc) => localizedSlug(cc.slug, cc.help_category_translations, loc));
          segs.push(localizedSlug(a.slug, a.help_article_translations, loc));
          pathByLocale[loc] = buildHelpHref(loc, segs);
        }
        out.push({ kind: "article", updated_at: a.updated_at, pathByLocale });
      }

      return { entries: out };
    } catch (e) {
      console.error("[help] getAllHelpUrls failed", e);
      return { entries: [] as Array<{ kind: "category" | "article"; updated_at?: string; pathByLocale: Record<string, string> }> };
    }
  });
